﻿import * as React from "react";
import { ITextFieldProps, TextField } from "../Components";
import { PointerEventsManager } from "../Managers";
import { StringUtil } from "../../Utilities/StringUtil";

export interface IEditablePropertyProps extends ITextFieldProps {
    callback: (propertyName: string, newValue: string) => void;
    name: string;
}

export interface IEditablePropertyState {
    editedValue: string;
    editingValue: string;
    isEditing: boolean;
}

export class EditableProperty extends React.Component<
    IEditablePropertyProps,
    IEditablePropertyState
> {
    constructor(props: IEditablePropertyProps) {
        super(props);

        this.state = {
            editedValue: this.props.value as string,
            editingValue: null,
            isEditing: false,
        };
    }

    componentWillUnmount(): void {
        PointerEventsManager.togglePointerEvents(true);
        document.removeEventListener(`mousedown`, this._handleClickOutside);
    }

    private _wrapperRef: HTMLDivElement;

    private _inputRef: HTMLInputElement;

    private readonly _startEditing = (): void => {
        this.setState(
            {
                editingValue: this.props.value as string,
                isEditing: true,
            },
            this._setFocus,
        );

        document.addEventListener(`mousedown`, this._handleClickOutside);
        PointerEventsManager.togglePointerEvents(false);
    };

    private readonly _setFocus = (): void => {
        this._inputRef.focus();
    };

    private readonly _inputChange = (event: React.ChangeEvent): void => {
        const target: HTMLInputElement = event.target as HTMLInputElement;

        this.setState({
            editingValue: target.value,
        });
    };

    private readonly _keyPress = (event: React.KeyboardEvent): void => {
        switch (event.keyCode) {
            // Enter key is pressed.
            case 13:
                this._changeDisplay();
                break;
            default:
                break;
        }
    };

    private readonly _changeDisplay = (): void => {
        PointerEventsManager.togglePointerEvents(true);
        document.removeEventListener(`mousedown`, this._handleClickOutside);

        this.setState(
            (prevState) => ({
                editedValue: prevState.editingValue,
                isEditing: false,
            }),
            this._submitChange,
        );
    };

    private readonly _submitChange = (): void => {
        this.props.callback(this.props.name, this.state.editedValue);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _handleClickOutside = (event: any): void => {
        if (this._wrapperRef && !this._wrapperRef.contains(event.target)) {
            this._changeDisplay();
        }
    };

    render(): React.ReactElement {
        const value: string =
            this.state.editedValue !== this.props.value
                ? this.state.editedValue
                : this.props.value;

        const mainDivClass = this.props.className ?? ``,
            outputProperty: string = StringUtil.isNullOrEmpty(value)
                ? this.props.placeholder
                : (this.props.value as string),
            propertyClassExtension: string =
                this.props.value == null ? `placeholder` : `value`;

        return (
            <div
                className={`editableproperty ${mainDivClass}`}
                ref={(d): HTMLDivElement => (this._wrapperRef = d)}
            >
                {!this.state.isEditing && (
                    <div
                        className={`editableproperty-${propertyClassExtension}`}
                        onClick={this._startEditing}
                    >
                        {outputProperty}
                    </div>
                )}
                {this.state.isEditing && (
                    <TextField
                        characterlimit={this.props.characterlimit}
                        className={`editableproperty-value`}
                        displaylimit={this.props.displaylimit}
                        inputRef={(i: HTMLInputElement): HTMLInputElement =>
                            (this._inputRef = i)
                        }
                        onChange={this._inputChange}
                        onKeyDown={this._keyPress}
                        placeholder={this.props.placeholder}
                        type={`text`}
                        value={this.state.editingValue}
                        variant={this.props.variant}
                    />
                )}
            </div>
        );
    }
}
